

import React, { useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormField } from "components/form/FormField/FormField";
import { Button } from "components/ui/Button";
import { DatePickerField } from "components/ui/DatePickerField/DatePickerField";
import { DialogFooter } from "components/ui/Dialog/DialogFooter";
import { Select } from "components/ui/Select/Select";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";
import { useStaffMembers } from "features/practice/hooks/useStaffMembers";
import { EDD_METHOD_OPTIONS } from "features/pregnancy/constants/eddMethodOptions";
import { useCreateFoetuses } from "features/pregnancy/hooks/useCreateFoetuses";
import { useCreatePregnancy } from "features/pregnancy/hooks/useCreatePregnancy";
import { calcEdd } from "features/pregnancy/utils/calcEdd";
import { PATIENT_CHECK_ANSWERS } from "features/pregnancy/utils/patientCheckAnswers";
import { addPregnancyFormSchema } from "features/pregnancy/utils/pregnancyFormSchema";
import { transformStaffOptions } from "features/pregnancy/utils/transformStaffOptions";
import { onInvalidSubmit } from "utils/reportFormErrors";

import style from "./AddPregnancyForm.module.css";
import { PatientChecksFields } from "./PatientChecksFields";

const today = new Date().toISOString().split("T")[0];

/*
 * One foetus, until the maternity hospital's messages say otherwise (KIN-2060, KIN-2057).
 *
 * Nobody can answer "how many" at a first visit — it takes a dating scan — and the backend now
 * reads the count off the hospital's messages, so the form neither asks nor guesses: it records the
 * one pregnancy it knows about. The value cannot be left out: the column carries a >= 1 check, and
 * the antenatal form draws one Foetal Exam section per foetus row, so a pregnancy with no foetus
 * row has nowhere to record a visit made before the hospital first wrote.
 *
 * The count itself is still an MN-CMS OBX (SCT 246435002, LOINC 55281-0, type NM) and the sole
 * trigger for the multiple-pregnancy variant, where presentation / heart / activity repeat per
 * foetus as "Baby A:value, Baby B:value". It stays editable in the Edit pregnancy modal.
 */
const FOETUS_COUNT_AT_CREATION = 1;

const AddPregnancyForm = ({
  currentPatientId,
  closeModal,
  tenantVal,
  onSuccess,
}) => {
  const { data: staffMembers } = useStaffMembers();
  const { data: staffInfo } = useStaffInfo();
  const form = useForm({
    resolver: zodResolver(addPregnancyFormSchema),
    defaultValues: {
      gravida: "",
      para: "",
      /* Nothing has been asked yet, so nothing is asserted — these go to the server as null
         rather than as four negatives nobody stated (KIN-2056). */
      hasClinicHCGPositive: PATIENT_CHECK_ANSWERS.notAsked,
      smoker: PATIENT_CHECK_ANSWERS.notAsked,
      noFamilyHistoryOfCongenitalAbnormality: PATIENT_CHECK_ANSWERS.notAsked,
      hasHadChickenPox: PATIENT_CHECK_ANSWERS.notAsked,
      lastMenstrualPeriod: today,
      eddMethod: "",
      estimatedDueDate: "",
      conductedByStaffMemberId: "",
    },
  });

  useEffect(() => {
    if (!staffInfo?.staffMember?.id) return;
    if (form.getValues("conductedByStaffMemberId")) return;
    form.setValue("conductedByStaffMemberId", String(staffInfo.staffMember.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffInfo]);

  const { mutate: createFoetuses } = useCreateFoetuses();

  const { mutate: addPregnancy, isPending } = useCreatePregnancy({
    patientId: currentPatientId,
    tenantVal,
    onSuccess: (response) => {
      const pregnancyId =
        response?.data?.data?.id ?? response?.data?.id ?? response?.id;

      if (pregnancyId) {
        createFoetuses({ pregnancyId, count: FOETUS_COUNT_AT_CREATION });
      }

      form.reset();

      if (onSuccess && pregnancyId) {
        onSuccess(pregnancyId);
      } else {
        closeModal();
      }
    },
  });

  return (
    <FormProvider {...form}>
      <form
        className={style.addPregnancyForm}
        onSubmit={form.handleSubmit(
          (values) =>
            addPregnancy({
              ...values,
              /* Stated here rather than left to the mutation's fallbacks, because neither is a
                 field on this form: the hospital is whatever its first message says it is, and
                 until that message arrives the pregnancy has none (KIN-2060). */
              numberOfFoetuses: FOETUS_COUNT_AT_CREATION,
              receivingFacilityCode: null,
            }),
          onInvalidSubmit("AddPregnancyForm")
        )}
      >
        {(() => {
          const isLmp =
            !form.watch("eddMethod") || form.watch("eddMethod") === "LMP";

          return (
            <>
              <div className={style.inputGroupGrid4}>
                <FormField name="lastMenstrualPeriod">
                  <label className={style.pregnancyFormLabel}>
                    Last Menstrual Period
                  </label>
                  <DatePickerField
                    value={form.watch("lastMenstrualPeriod")}
                    onChange={(val) =>
                      form.setValue("lastMenstrualPeriod", val, {
                        shouldValidate: true,
                      })
                    }
                  />
                </FormField>
                <FormField name="conductedByStaffMemberId">
                  <label className={style.pregnancyFormLabel}>Doctor</label>
                  <Select
                    options={transformStaffOptions(staffMembers)}
                    value={form.watch("conductedByStaffMemberId") ?? ""}
                    onChange={(value) =>
                      form.setValue("conductedByStaffMemberId", value, {
                        shouldValidate: true,
                      })
                    }
                    placeholder="Select doctor"
                    fullWidth
                  />
                </FormField>
                <FormField name="eddMethod">
                  <label className={style.pregnancyFormLabel}>
                    Estimated Due Date Method
                  </label>
                  <Controller
                    control={form.control}
                    name="eddMethod"
                    render={({ field }) => (
                      <Select
                        name="eddMethod"
                        options={EDD_METHOD_OPTIONS}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Select EDD method"
                        fullWidth
                      />
                    )}
                  />
                </FormField>
                <FormField name="estimatedDueDate">
                  <label className={style.pregnancyFormLabel}>
                    Estimated Due Date
                  </label>
                  <DatePickerField
                    value={
                      isLmp
                        ? calcEdd(form.watch("lastMenstrualPeriod"))
                        : form.watch("estimatedDueDate")
                    }
                    onChange={(val) =>
                      form.setValue("estimatedDueDate", val, {
                        shouldValidate: true,
                      })
                    }
                    disabled={isLmp}
                  />
                </FormField>
              </div>
              {/* Four columns with two fields in them, not a two-column row: Gravida and Para keep
                  the width and the left edge of the row above, so the space the hospital left
                  reads as the end of a row rather than as two stretched inputs. */}
              <div className={style.inputGroupGrid4}>
                <FormField name="gravida">
                  <label className={style.pregnancyFormLabel}>Gravida</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="form-control"
                    onInput={(e) =>
                      (e.target.value = e.target.value.replace(/[^0-9]/g, ""))
                    }
                    {...form.register("gravida")}
                  />
                </FormField>
                <FormField name="para">
                  <label className={style.pregnancyFormLabel}>Para</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="form-control"
                    onInput={(e) =>
                      (e.target.value = e.target.value.replace(/[^0-9]/g, ""))
                    }
                    {...form.register("para")}
                  />
                </FormField>
              </div>
            </>
          );
        })()}

        <PatientChecksFields control={form.control} />

        <DialogFooter className={style.formFooter}>
          <Button variant="outline" onClick={closeModal} type="button">
            Cancel
          </Button>
          <Button type="submit" disabled={isPending}>
            Add
          </Button>
        </DialogFooter>
      </form>
    </FormProvider>
  );
};

export { AddPregnancyForm };
